import { Box, Typography } from "@mui/material";
import { Bar, CartesianGrid, ComposedChart, LabelList, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useLegendToggle } from "./useLegendToggle";

// Gráfico combinado: UMA barra (eixo direito, ex.: gasto) + N linhas (eixo esquerdo, ex.: custos por objetivo).
// Réplica do "Mkt Cost Per Goal" do Zoho (gasto em barra, custos por lead/avaliação/booking/tratamento em linha).
// countKey/countLabel: mostram no tooltip o denominador usado (ex.: "351,04 € · 31 tratamentos").
// countDetail: acrescenta um detalhe entre parênteses a essa contagem (ex.: "31 tratamentos (em aberto 5)").
type ComboSeries = {
    key: string;
    label: string;
    color: string;
    countKey?: string;
    countLabel?: string;
    countDetail?: { key: string; label: string };
    valueFormatter?: (value: number) => string;
};

type ComboTooltipEntry = { dataKey: string; name: string; value: number | string; color: string; payload: Record<string, string | number> };

const renderComboTooltip = ({
    active,
    payload,
    label,
    seriesByKey,
}: {
    active?: boolean;
    payload?: Array<ComboTooltipEntry>;
    label?: string;
    seriesByKey: Map<string, ComboSeries>;
}) => {
    if (!active || !payload || payload.length === 0) return null;
    const rows = payload.map((entry) => {
        const serie = seriesByKey.get(entry.dataKey);
        const numericValue = Number(entry.value);
        const valueText = serie?.valueFormatter ? serie.valueFormatter(numericValue) : String(entry.value);
        const countText = serie?.countKey !== undefined ? ` · ${Number(entry.payload?.[serie.countKey]) || 0} ${serie.countLabel || ""}`.trimEnd() : "";
        const countDetailText =
            serie?.countKey !== undefined && serie.countDetail !== undefined
                ? ` (${serie.countDetail.label} ${Number(entry.payload?.[serie.countDetail.key]) || 0})`
                : "";
        const row = (
            <Box key={entry.dataKey} sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: entry.color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: "#333" }}>
                    {entry.name}: <strong>{valueText}</strong>
                    {countText}
                    {countDetailText}
                </Typography>
            </Box>
        );
        return row;
    });
    const tooltip = (
        <Box sx={{ bgcolor: "#fff", border: "1px solid #d0d0d0", borderRadius: 1, px: 1.25, py: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#111" }}>
                {label}
            </Typography>
            {rows}
        </Box>
    );
    return tooltip;
};

type ComboBarLineChartProps = {
    points: Array<Record<string, string | number>>;
    bar: ComboSeries;
    lines: Array<ComboSeries>;
    categoryKey?: string;
    height?: number;
    showPointLabels?: boolean;
    showBarLabels?: boolean;
    barValueFormatter?: (value: number) => string;
};

export const ComboBarLineChart = ({
    points,
    bar,
    lines,
    categoryKey = "monthLabel",
    height = 320,
    showPointLabels = true,
    showBarLabels = true,
    barValueFormatter,
}: ComboBarLineChartProps) => {
    const { hiddenKeys, legendProps } = useLegendToggle();
    const seriesByKey = new Map<string, ComboSeries>();
    seriesByKey.set(bar.key, bar);
    lines.forEach((serie) => seriesByKey.set(serie.key, serie));
    return (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={points} margin={{ top: 24, right: 24, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey={categoryKey} tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                <Tooltip content={(tooltipProps) => renderComboTooltip({ ...(tooltipProps as object), seriesByKey })} />
                <Legend {...legendProps} />
                <Bar yAxisId="right" dataKey={bar.key} name={bar.label} fill={bar.color} fillOpacity={0.45} isAnimationActive={false} hide={hiddenKeys.has(bar.key)}>
                    {showBarLabels && (
                        <LabelList
                            dataKey={bar.key}
                            position="top"
                            fontSize={10}
                            fill="#888"
                            formatter={(value: number) => {
                                const numericValue = Number(value);
                                if (!numericValue) return "";
                                return barValueFormatter ? barValueFormatter(numericValue) : String(numericValue);
                            }}
                        />
                    )}
                </Bar>
                {lines.map((serie) => (
                    <Line
                        key={serie.key}
                        yAxisId="left"
                        type="monotone"
                        dataKey={serie.key}
                        name={serie.label}
                        stroke={serie.color}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        isAnimationActive={false}
                        connectNulls={false}
                        hide={hiddenKeys.has(serie.key)}
                    >
                        {showPointLabels && <LabelList dataKey={serie.key} position="top" fontSize={10} fill={serie.color} />}
                    </Line>
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
};
